import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Header } from '@tanstack/react-table';

import { ChevronDownIcon } from '../../../../../assets';
import { ExtendedCheckbox } from '../../../../ExtendedCheckbox';
import { Popover } from '../../../../Popover';
import { EFilterType } from '../../../types/table';

import styles from './MultiSelectFilter.module.scss';

export interface IMultiSelectFilterProps {
    header: Header<any, unknown>;
    type: EFilterType;
    inline?: boolean;
}

export const MultiSelectFilter: React.FC<IMultiSelectFilterProps> = ({ header: { column }, inline = false }) => {
    const { t } = useTranslation();
    const selectedValues = useMemo(() => {
        const filterValue = column.getFilterValue();
        return Array.isArray(filterValue) ? (filterValue as string[]) : [];
    }, [column.getFilterValue()]);

    const options = useMemo(() => {
        const filtering = column.columnDef.meta?.filtering;
        if (filtering && filtering.type === EFilterType.multiSelect && 'options' in filtering) {
            return filtering.options;
        }
        return [];
    }, [column.columnDef.meta?.filtering]);

    const handleToggle = (value: string) => {
        const nextValues = selectedValues.includes(value)
            ? selectedValues.filter((selectedValue) => selectedValue !== value)
            : [...selectedValues, value];
        column.setFilterValue(nextValues.length > 0 ? nextValues : null);
    };

    const checkboxes = (
        <div className={styles.options}>
            {options.map(({ value, label }) => (
                <ExtendedCheckbox
                    key={value}
                    checked={selectedValues.includes(value)}
                    label={label}
                    onCheckedChange={() => handleToggle(value)}
                />
            ))}
        </div>
    );

    if (!inline) return checkboxes;

    const selectedLabels = options
        .filter(({ value }) => selectedValues.includes(value))
        .map(({ label }) => label)
        .join(', ');

    return (
        <Popover
            label={typeof column.columnDef.header === 'string' ? column.columnDef.header : undefined}
            triggerClassName={styles.triggerButton}
            trigger={
                <span className={styles.triggerContent}>
                    <span className={selectedLabels ? styles.value : styles.placeholder}>
                        {selectedLabels || t('table.filtering.select')}
                    </span>
                    <ChevronDownIcon className={styles.chevron} />
                </span>
            }
            contentProps={{ align: 'start', sideOffset: 4 }}
        >
            {checkboxes}
        </Popover>
    );
};
